export interface ApiErrorShape {
  // The v5 API returns errors as an array (full_messages) but a few paths
  // return a bare string, so accept both.
  errors?: string[] | string
  message?: string
  error?: string
}

export const SERVER_BUSY_MESSAGE =
  'Our servers are busy right now. Please wait a moment and try again.'

/**
 * Parse a fetch Response body as JSON without throwing on a non-JSON payload.
 *
 * When the origin stalls, CloudFront (30s origin timeout) returns an HTML error
 * page. Calling response.json() on that throws `SyntaxError: Unexpected token
 * '<', "<!doctype"...`, which previously crashed signup and left users stuck.
 * This checks the content-type and falls back to {} so callers can branch on
 * response.ok instead of on a parse crash.
 */
export const safeJson = async <T = unknown>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    return {} as T
  }
  try {
    const text = await response.text()
    return (text ? JSON.parse(text) : {}) as T
  } catch {
    return {} as T
  }
}

/**
 * Derive a user-facing message from a failed JSON API response, falling back to
 * a friendly message when the body carried no error detail (e.g. a gateway
 * timeout HTML page parsed to {}).
 */
export const apiErrorMessage = (
  data: ApiErrorShape,
  fallback: string = SERVER_BUSY_MESSAGE,
): string => {
  const firstError = Array.isArray(data.errors) ? data.errors[0] : data.errors
  return data.message || firstError || data.error || fallback
}
